import { matchesChord } from './chord';

/**
 * Keyboard and open/close behaviour for the ⌘K palette. The server owns the results; this owns
 * only what must not cost a round-trip — opening, the active row, and Enter.
 *
 * The active option is tracked as an index and surfaced through the input's aria-activedescendant,
 * never by moving DOM focus: this is a combobox, so focus stays in the text field while the user
 * arrows through results and keeps typing.
 */
export default () => ({
    open: false,
    activeIndex: 0,
    previouslyFocused: null,

    /**
     * Whichever resting trigger is on screen at this breakpoint: the field-shaped button above md,
     * the icon below it.
     *
     * Through Alpine refs rather than a querySelectorAll over $el. Livewire re-renders this
     * component while the popup is open, and after a morph $el can still point at the node the
     * morph replaced — a scan of it finds buttons that are no longer in the document, every one of
     * which reports offsetParent === null, so the scan returned nothing while $refs.trigger was
     * live, visible and focusable in the very same frame. Alpine re-binds refs on morph; $el is
     * the thing that goes stale.
     */
    visibleTrigger() {
        return [this.$refs.trigger, this.$refs.triggerCompact]
            .find((el) => el && el.isConnected && el.offsetParent !== null) ?? null;
    },

    /** Focus left the popup on its own (Tab, click away) — close without yanking it back. */
    dismiss() {
        this.close(false);
    },

    /**
     * Close once focus has genuinely left the popup, rather than on every focusout.
     *
     * Deferred a tick on purpose: choosing a filter is a Livewire round trip, and during the morph
     * focus lands on <body> for a moment before it settles back in the input. Reading
     * relatedTarget synchronously — the usual shape of this check — sees that transient null and
     * closes the popup the instant the user picks a filter.
     *
     * Tested against refs rather than $el.contains(): after a morph $el can still point at the
     * node the morph replaced, so containment answers about a detached tree.
     */
    dismissIfFocusLeft() {
        setTimeout(() => {
            if (!this.open) {
                return;
            }

            const active = document.activeElement;

            // Focus parked on <body> is not the user leaving — it is what a browser does when a
            // mousedown lands on something it will not focus, and reading it as an exit closed the
            // palette on clicks INSIDE itself. Measured in both engines: a plain <button> parks on
            // <body> in WebKit (which is why the filter pills carry @mousedown.prevent) and
            // non-focusable panel chrome — the "Suggested" heading, the empty state, the truncation
            // notice, the icons, the padding — parks there in WebKit AND Chromium. Result rows are
            // unaffected either way; their tabindex="-1" makes both engines mouse-focus them.
            //
            // Nothing that should dismiss is lost: a genuine click outside is already handled by
            // @click.outside on the root, and keyboard Tab always lands on a real element.
            if (!active || active === document.body) {
                return;
            }

            const inside = this.$refs.panel?.contains(active)
                || active === this.$refs.trigger
                || active === this.$refs.triggerCompact;

            if (!inside) {
                this.dismiss();
            }
        }, 0);
    },

    hotkey(event) {
        if (!matchesChord(event, { code: 'KeyK' })) {
            return;
        }

        // Claim the chord before the repeat check, so holding the keys down does not leak a
        // stream of Cmd+K through to the browser's own address-bar search.
        event.preventDefault();

        if (event.repeat) {
            return;
        }

        this.open ? this.close() : this.reveal();
    },

    reveal() {
        this.previouslyFocused = document.activeElement;
        this.open = true;
        this.activeIndex = 0;

        // First open only — until then the server short-circuits the searches, so a page where
        // nobody presses Cmd+K pays nothing for the palette. See QuickSearch::$active.
        if (!this.$wire.active) {
            this.$wire.activate();
        }

        this.$nextTick(() => this.$refs.input?.focus());
    },

    close(restoreFocus = true) {
        if (!this.open) {
            return;
        }

        this.open = false;

        // Return focus to whatever was focused when the palette opened (WCAG 2.4.3). The input
        // lives inside the x-show container, so hiding it drops focus to <body> and a keyboard
        // user restarts tabbing from the top of the page.
        //
        // Deferred because the usual target is a resting trigger, and which of the two is
        // displayed is decided by the md breakpoint (`hidden md:flex` / `md:hidden`), not by
        // `open` — neither is x-show'd; both stay mounted. Layout still has to have settled before
        // either can be measured or focused.
        //
        // `restoreFocus` is false when the popup closed because focus already left it (Tab, or a
        // click elsewhere): dragging focus back would undo the move the user just made.
        const previous = this.previouslyFocused;
        this.previouslyFocused = null;

        if (!restoreFocus) {
            return;
        }

        // On the next FRAME, not the next tick. Both the fallback's visibility test and the focus
        // call read layout: visibleTrigger() picks whichever trigger the breakpoint is showing by
        // its offsetParent, and an element still resolving to display:none answers nothing and
        // cannot take focus. $nextTick was measured doing exactly that — the element was focusable
        // a moment later, and calling .focus() by hand at that point worked, but the scheduled call
        // had already run and done nothing. requestAnimationFrame is after style and layout, so it
        // cannot be early.
        //
        // Fall back to the trigger when there is nothing meaningful to go back to — opening with
        // the shortcut from a page with nothing focused leaves document.body as the "previous"
        // element, and handing focus back to the body strands a keyboard user at the top of the
        // document.
        requestAnimationFrame(() => {
            const target = (previous && previous.isConnected && previous !== document.body)
                ? previous
                : this.visibleTrigger();

            if (target && typeof target.focus === 'function') {
                target.focus();
            }
        });
    },

    /**
     * Read the options out of the DOM rather than mirroring the server's list in Alpine state:
     * Livewire re-renders the list on every keystroke, and the DOM is the only copy guaranteed to
     * be the one the user is looking at. The empty state is a non-focusable aria-disabled option,
     * so it is excluded — otherwise Enter on "no matches" would try to activate it.
     */
    options() {
        return [...(this.$refs.list?.querySelectorAll('[role="option"]:not([aria-disabled="true"])') ?? [])];
    },

    count() {
        return this.options().length;
    },

    move(delta) {
        const total = this.count();

        if (!total) {
            return;
        }

        this.activeIndex = (this.activeIndex + delta + total) % total;
        this.scrollActiveIntoView();
    },

    jump(index) {
        const total = this.count();

        if (!total) {
            return;
        }

        this.activeIndex = Math.max(0, Math.min(total - 1, index));
        this.scrollActiveIntoView();
    },

    /**
     * Enter clicks the active option. A link result navigates and a workspace result runs its
     * Livewire action — one key path for both, so neither can drift from the other.
     */
    choose() {
        this.options()[this.activeIndex]?.click();
    },

    scrollActiveIntoView() {
        this.$nextTick(() => {
            this.options()[this.activeIndex]?.scrollIntoView({ block: 'nearest' });
        });
    },
});
